import React, { useEffect, useState, memo, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  fetchTransactions,
  getTransactionDetails,
  searchTransactions,
  clearSearchResults,
  clearSelectedTransaction,
  selectTransactions,
  selectSearchResults,
  selectSelectedTransaction,
  selectLoadingStates,
  selectErrors,
  selectPagination,
  selectSearchPagination,
  selectIsSearchMode,
} from "../../RTK/transaction/GetTransaction";
import { usePermissions } from "../../contexts/PermissionsContext";
import {
  DollarSign,
  AlertCircle,
  Clock,
  CheckCircle,
  Search,
  Calendar,
  Download,
  Filter,
  Loader2,
  Copy,
  CalendarClock,
  User,
  Package,
  XCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  FileText,
  Lock,
} from "lucide-react";
import TransactionDetailsModal from "./popups/TransactionDetailsModal";
import { useNavigate } from "react-router-dom";
import { ProtectedComponent } from "../../components/ProtectedComponent/ProtectedComponent";

// Utility function for color classes
const getColorClasses = (color) => {
  const classes = {
    yellow: "bg-yellow-500/10 text-yellow-500",
    red: "bg-red-500/10 text-red-500",
    blue: "bg-blue-500/10 text-blue-500",
    green: "bg-green-500/10 text-green-500",
    gray: "bg-gray-500/10 text-gray-500",
  };
  return classes[color] || classes.gray;
};

// Loading Spinner Component
const LoadingSpinner = memo(({ text }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-2">
    <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
    <span className="text-gray-400">{text}</span>
  </div>
));

// Empty State Component
const EmptyState = memo(() => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
    <Package className="w-12 h-12 text-gray-500" />
    <p className="text-gray-500 text-lg">لا توجد معاملات</p>
  </div>
));

// Error State Component
const ErrorState = memo(({ message }) => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
    <AlertCircle className="w-12 h-12 text-red-500" />
    <p className="text-red-500 text-lg">{message}</p>
  </div>
));

// Stats Card Component
const StatsCard = memo(
  ({ title, value, icon: Icon, color, trend, walletBalance, debt }) => (
    <div className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-gray-700 transition-all duration-300">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-400 text-sm">{title}</p>
          <h3 className="text-2xl font-bold text-white mt-1">
            {typeof value === "number" ? value.toLocaleString() : value}
          </h3>
        </div>
        <div className={`p-3 rounded-lg ${getColorClasses(color)}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-800">
        <div className="text-sm 2xl:text-xs">
          <span className="text-gray-400">الرصيد الفعلي: </span>
          <span className="text-white font-medium">
            ${walletBalance?.toLocaleString() || "0.00"}
          </span>
        </div>
        <div className="text-sm 2xl:text-xs">
          <span className="text-gray-400">الدين الفعلي: </span>
          <span className="text-white font-medium">
            ${debt?.toLocaleString() || "0.00"}
          </span>
        </div>
      </div>
    </div>
  ),
);

const TransactionCard = memo(({ transaction, onClick, canViewDetails }) => {
  // Status utilities
  const getStatusDetails = (status) => {
    switch (status) {
      case "paid":
        return { color: "bg-green-500/10 text-green-500", text: "مكتملة" };
      case "failed":
        return { color: "bg-red-500/10 text-red-500", text: "فاشلة" };
      default:
        return { color: "bg-yellow-500/10 text-yellow-500", text: "معلقة" };
    }
  };

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // ✅ فحص الصلاحية
    if (!canViewDetails) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل المعاملات 🔒");
      return;
    }

    if (transaction?.id) {
      onClick(transaction.id);
    }
  };

  const { color, text } = getStatusDetails(transaction?.status);

  return (
    <div
      onClick={handleCardClick}
      className={`bg-gray-900 rounded-xl border border-gray-800 ${
        canViewDetails
          ? "hover:border-yellow-500/50 cursor-pointer"
          : "cursor-not-allowed opacity-70"
      } transition-all duration-300 overflow-hidden group relative`}
    >
      {/* ✅ أيقونة قفل */}
      {!canViewDetails && (
        <div className="absolute top-2 left-2 bg-slate-700/90 p-1.5 rounded-lg z-10">
          <Lock className="w-3 h-3 text-slate-400" />
        </div>
      )}
      {/* Card Header */}
      <div className="p-4 border-b border-gray-800 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-full overflow-hidden bg-gray-800"
            dangerouslySetInnerHTML={{
              __html: transaction?.user?.profile_image,
            }}
          />
          <div>
            <h3 className="text-white font-medium">
              {transaction?.user?.username || "Unknown User"}
            </h3>
            <span className="text-sm text-gray-400">
              رصيد: ${transaction?.user?.balance || "0.00"}
            </span>
          </div>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-medium ${color}`}
        >
          {text}
        </span>
      </div>

      {/* Card Content */}
      <div className="p-4 space-y-3">
        <div className="flex justify-between items-center">
          <span className="text-gray-400">المبلغ</span>
          <span className="text-white font-bold">
            ${Number(transaction?.amount || 0).toFixed(2)}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-400">طريقة الدفع</span>
          <div className="flex items-center gap-2">
            {transaction?.payment_method?.logo && (
              <img
                src={transaction.payment_method.logo}
                alt={transaction.payment_method.name}
                className="w-6 h-6 rounded"
              />
            )}
            <span className="text-white">
              {transaction?.payment_method?.name || "Unknown"}
            </span>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-400">رقم المعاملة</span>
          <span className="text-white group-hover:text-yellow-500 transition-colors">
            {transaction?.transaction_id || "N/A"}
          </span>
        </div>

        <div className="flex justify-between items-center">
          <span className="text-gray-400">التاريخ</span>
          <div className="flex items-center gap-2 text-gray-300">
            <CalendarClock className="w-4 h-4" />
            <span>{transaction?.created_at || "N/A"}</span>
          </div>
        </div>
      </div>

      {/* Hover Overlay */}
      {canViewDetails && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <span className="bg-yellow-500 text-black px-4 py-2 rounded-lg transform translate-y-4 group-hover:translate-y-0 transition-transform">
            عرض التفاصيل
          </span>
        </div>
      )}
    </div>
  );
});

// Pagination Component
const Pagination = memo(({ pagination, onPageChange, isLoading }) => {
  const { currentPage, totalPages, hasNext, hasPrevious, totalCount } =
    pagination;

  if (totalPages <= 1) return null;

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let start = Math.max(1, currentPage - Math.floor(showPages / 2));
    let end = Math.min(totalPages, start + showPages - 1);

    if (end - start < showPages - 1) {
      start = Math.max(1, end - showPages + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="text-gray-400 text-sm">
          عرض النتائج من {(currentPage - 1) * pagination.pageSize + 1} إلى{" "}
          {Math.min(currentPage * pagination.pageSize, totalCount)} من أصل{" "}
          {totalCount}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!hasPrevious || isLoading}
            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {getPageNumbers().map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                page === currentPage
                  ? "bg-yellow-500 text-black"
                  : "bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700"
              } disabled:cursor-not-allowed`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!hasNext || isLoading}
            className="p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
});

// Filters Section Component
const FiltersSection = memo(
  ({
    searchQuery,
    setSearchQuery,
    filter,
    setFilter,
    isSearching,
    onReset,
    navigate,
    canSearch,
    canViewReport,
  }) => (
    <div className="bg-gray-900 p-6 rounded-xl border border-gray-800">
      <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
        <h2 className="text-xl font-bold text-white">المعاملات</h2>

        <div className="flex flex-wrap items-center gap-4">
          {/* Report Button */}
          {/* ✅ Report Button - محمي */}
          {canViewReport ? (
            <button
              onClick={() => navigate("/transaction/report")}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-lg hover:shadow-xl"
            >
              <FileText className="w-5 h-5" />
              <span>تقرير المعاملات</span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
              <Lock className="w-5 h-5 text-slate-500" />
              <span className="text-slate-400 text-sm">
                ليس لديك صلاحية لعرض التقرير
              </span>
            </div>
          )}

          {/* ✅ حماية شريط البحث */}
          <ProtectedComponent
            permission="search_transactions"
            fallback={
              <div className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5 text-slate-500" />
                <span className="text-slate-400 text-sm">
                  ليس لديك صلاحية البحث
                </span>
              </div>
            }
          >
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="ابحث عن المعاملات..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-80 bg-gray-800 text-white pr-10 pl-4 py-2 rounded-lg border border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition-all"
              />
              {isSearching && (
                <Loader2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-yellow-500 animate-spin" />
              )}
            </div>
          </ProtectedComponent>

          <select
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-yellow-500"
            value={filter.days}
            onChange={(e) => setFilter({ ...filter, days: e.target.value })}
            disabled={!!searchQuery.trim()}
          >
            <option value="1">آخر 1 أيام</option>
            <option value="30">آخر 30 يوم</option>
            <option value="90">آخر 90 يوم</option>
          </select>

          <select
            className="bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-700 focus:border-yellow-500"
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            disabled={!!searchQuery.trim()}
          >
            <option value="all">كل الحالات</option>
            <option value="paid">مكتملة</option>
            <option value="failed">فاشلة</option>
            <option value="initial">معلقة</option>
          </select>

          <button
            className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors"
            onClick={onReset}
          >
            <RefreshCw className="w-5 h-5" />
            إعادة تعيين
          </button>
        </div>
      </div>
    </div>
  ),
);

// Main Component
const TransactionsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  // ✅ فحص الصلاحيات
  const canViewTransactionDetails = hasPermission("view_transaction_details");
  const canUpdateTransactionStatus = hasPermission("update_transaction_status");
  const canSearchTransactions = hasPermission("search_transactions");
  const canViewDailyTransactionsSummary = hasPermission(
    "view_daily_transactions_summary",
  );

  const data = useSelector(selectTransactions);
  const searchResults = useSelector(selectSearchResults);
  const selectedTransaction = useSelector(selectSelectedTransaction);
  const { isLoading, isSearching, isLoadingDetails } =
    useSelector(selectLoadingStates);
  const { error, searchError, detailsError } = useSelector(selectErrors);
  const pagination = useSelector(selectPagination);
  const isSearchMode = useSelector(selectIsSearchMode);

  const [searchQuery, setSearchQuery] = useState("");
  const [searchPage, setSearchPage] = useState(1);
  const [filter, setFilter] = useState({
    days: 1,
    status: "all",
    paymentMethod: "all",
  });

  const pageSize = 20;

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        if (!canSearchTransactions) {
          toast.error("ليس لديك صلاحية البحث في المعاملات 🔒");
          return;
        }

        if (query.trim().length >= 2) {
          dispatch(searchTransactions(query.trim()));
          setSearchPage(1);
        } else if (query.trim().length === 0) {
          dispatch(clearSearchResults());
        }
      }, 500),
    [dispatch, canSearchTransactions],
  );

  // Handle search query changes
  useEffect(() => {
    if (searchQuery !== undefined) {
      debouncedSearch(searchQuery);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [searchQuery, debouncedSearch]);

  // Initial fetch
  useEffect(() => {
    if (!searchQuery) {
      dispatch(
        fetchTransactions({
          filter,
          page: 1,
          pageSize: 20,
        }),
      )
        .unwrap()
        .catch((error) => {
          console.error("Error fetching transactions:", error);
          toast.error("فشل في تحميل المعاملات");
        });
    }
  }, [dispatch, filter]);

  const handleCardClick = (id) => {
    if (!canViewTransactionDetails) {
      toast.error("ليس لديك صلاحية لعرض تفاصيل المعاملات 🔒");
      return;
    }

    dispatch(getTransactionDetails(id))
      .unwrap()
      .catch((error) => {
        toast.error("فشل في تحميل تفاصيل المعاملة");
        console.error("Error loading transaction details:", error);
      });
  };

  const handleFilterReset = () => {
    setFilter({ days: 1, status: "all", paymentMethod: "all" });
    setSearchQuery("");
    setSearchPage(1);
    dispatch(clearSearchResults());
  };

  const handleModalClose = () => {
    dispatch(clearSelectedTransaction());
  };

  const handlePageChange = (page) => {
    if (isSearchMode && searchQuery.trim()) {
      // For search results, just update the client-side page
      setSearchPage(page);
    } else {
      // For regular results, fetch from server
      dispatch(
        fetchTransactions({
          filter,
          page,
          pageSize: 20,
        }),
      );
    }
  };

  // Updated client-side pagination for search results
  const getSearchPagination = (searchData, currentPage, pageSize) => {
    // Extract transactions array from search results
    const transactions = searchData?.transactions || [];

    // ✅ عكس الترتيب - من الأحدث للأقدم
    const sortedTransactions = [...transactions].reverse();

    const totalCount = sortedTransactions.length;
    const totalPages = Math.ceil(totalCount / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedData = sortedTransactions.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      pagination: {
        currentPage,
        pageSize,
        totalCount,
        totalPages,
        hasNext: currentPage < totalPages,
        hasPrevious: currentPage > 1,
      },
    };
  };

  // Updated data handling logic
  let currentData;
  let currentPagination;
  let transactionsToShow;

  if (isSearchMode && searchResults) {
    // Search mode: use client-side pagination on search results
    console.log("Search mode - searchResults:", searchResults);
    const searchPaginationResult = getSearchPagination(
      searchResults,
      searchPage,
      pageSize,
    );
    transactionsToShow = searchPaginationResult.data;
    currentPagination = searchPaginationResult.pagination;
    currentData = searchResults;
  } else {
    // Regular mode: use server-side pagination
    console.log("Regular mode - data:", data);
    currentData = data;
    currentPagination = pagination;
    transactionsToShow = data?.results?.transactions || [];
  }

  // Updated stats cards to handle different data structures
  const getStatsData = () => {
    if (isSearchMode && searchResults) {
      // For search results, calculate stats from the transactions array
      const transactions = searchResults?.transactions || [];
      return {
        summary: {
          total_amount: transactions.reduce(
            (sum, t) => sum + parseFloat(t.amount || 0),
            0,
          ),
          total_failed_transactions: transactions.filter(
            (t) => t.status === "failed",
          ).length,
          total_initial_transactions: transactions.filter(
            (t) => t.status === "initial",
          ).length,
          total_paid_transactions: transactions.filter(
            (t) => t.status === "paid",
          ).length,
          total_adjusted_wallet: 0,
          total_debt: 0,
        },
      };
    }
    return data?.results || {};
  };

  const statsData = getStatsData();
  const statsCards = [
    {
      title: "إجمالي المبلغ",
      value: statsData?.summary?.total_amount || 0,
      icon: DollarSign,
      color: "yellow",
      walletBalance: statsData?.summary?.total_adjusted_wallet || 0,
      debt: statsData?.summary?.total_debt || 0,
    },
    {
      title: "المعاملات الفاشلة",
      value: statsData?.summary?.total_failed_transactions || 0,
      icon: XCircle,
      color: "red",
      walletBalance: statsData?.summary?.total_adjusted_wallet || 0,
      debt: statsData?.summary?.total_debt || 0,
    },
    {
      title: "المعاملات المعلقة",
      value: statsData?.summary?.total_initial_transactions || 0,
      icon: Clock,
      color: "blue",
      walletBalance: statsData?.summary?.total_adjusted_wallet || 0,
      debt: statsData?.summary?.total_debt || 0,
    },
    {
      title: "المعاملات المكتملة",
      value: statsData?.summary?.total_paid_transactions || 0,
      icon: CheckCircle,
      color: "green",
      walletBalance: statsData?.summary?.total_adjusted_wallet || 0,
      debt: statsData?.summary?.total_debt || 0,
    },
  ];

  // Updated search results count
  const getSearchResultsCount = () => {
    return searchResults?.transactions?.length || 0;
  };

  return (
    <div className="p-6 space-y-6" dir="rtl">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((stat, index) => (
          <StatsCard key={index} {...stat} />
        ))}
      </div>

      {/* Filters */}

      <FiltersSection
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        filter={filter}
        setFilter={setFilter}
        isSearching={isSearching}
        onReset={handleFilterReset}
        navigate={navigate}
        canSearch={canSearchTransactions}
        canViewReport={canViewDailyTransactionsSummary}
      />

      {/* Search Results Info */}
      {isSearchMode && searchResults && (
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2 text-blue-400">
            <Search className="w-5 h-5" />
            <span>
              تم العثور على {getSearchResultsCount()} نتيجة للبحث عن "
              {searchQuery}"
            </span>
          </div>
        </div>
      )}

      {/* Content */}
      {isLoading || isSearching ? (
        <LoadingSpinner
          text={
            isSearching
              ? "جاري البحث في جميع المعاملات..."
              : "جاري تحميل المعاملات..."
          }
        />
      ) : error || searchError ? (
        <ErrorState message={error || searchError} />
      ) : transactionsToShow.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {transactionsToShow.map((transaction) => (
              <TransactionCard
                key={transaction.id}
                transaction={transaction}
                onClick={handleCardClick}
                canViewDetails={canViewTransactionDetails}
              />
            ))}
          </div>

          {/* Pagination */}
          <Pagination
            pagination={currentPagination}
            onPageChange={handlePageChange}
            isLoading={isLoading || isSearching}
          />
        </>
      )}

      {/* Transaction Details Modal */}
      {selectedTransaction && canViewTransactionDetails && (
        <TransactionDetailsModal
          isOpen={!!selectedTransaction}
          onClose={handleModalClose}
          transactionData={selectedTransaction}
          canUpdateStatus={canUpdateTransactionStatus}
        />
      )}

      {/* Loading Details Overlay */}
      {isLoadingDetails && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
            <span className="text-white">جاري تحميل التفاصيل...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(TransactionsPage);
